import { Box } from '@mui/material';
import { WidgetWrapper } from '../WidgetWrapper';
import {
  cardGraphicLevel,
  cardGraphicText,
  cardGraphic,
  cardGraphicInner,
  cardGraphicLabel,
  cardGraphicLabelLeft,
  cardGraphicLabelRight,
} from '../style';
import { isAlarmLevelValid, transferValueToNewRange } from '../../utils';
import { AlarmType } from '../../types';
import { AssetType } from 'src/features/sites/types/types';
import { WET_WELL_TAG_NAME } from 'src/features/sites';
import { useGetLatestGatewayReadings } from 'src/hooks/useGetLatestGatewayReadings';

type Props = {
  height: number;
  waterLevel: number | null;
  title: string;
  unit: string;
  alarms: AlarmType[];
  isEditMode?: boolean;
  widgetId?: string;
  toggleAddOrRemoveHandler: () => void;
  testId?: string;
  asset: AssetType | undefined;
  timestamp: string | null;
};

export const LiftStationWetWell = ({
  height,
  waterLevel,
  title,
  alarms,
  unit,
  isEditMode,
  widgetId,
  toggleAddOrRemoveHandler,
  testId,
  asset,
  timestamp,
}: Props) => {
  const waterTowerAsset = asset?.assignedTags?.find(
    (tag) => tag?.tagName?.toLowerCase() === WET_WELL_TAG_NAME
  );

  const assignedTagId = waterTowerAsset?.assignedTagId;
  const gatewayId = waterTowerAsset?.assignedGatewayId;

  const { readings: latestReadings } = useGetLatestGatewayReadings({
    gatewayId: gatewayId,
    tagId: assignedTagId,
  });
  const readingsRawInputName = assignedTagId && latestReadings[assignedTagId];
  const readingsValues = readingsRawInputName?.values;
  const readingValueList = readingsValues && Object.values(readingsValues);
  const readingKeysList = readingsValues && Object.keys(readingsValues);

  const readingValue = readingValueList && readingValueList[0];
  const roundedWaterLevel = readingValue !== false ? Math.round(readingValue * 10) / 10 : -1;
  const initialLatestTimestamp = readingKeysList?.length && readingKeysList[0];

  const waterLevelData =
    waterLevel !== null && waterLevel !== undefined ? waterLevel : Number(roundedWaterLevel);

  const HEIGHT_DISPLAY = 100;
  const validLevel = waterLevelData !== null && waterLevelData !== -1 && !isNaN(waterLevelData);
  const waterLevelDisplay = validLevel
    ? transferValueToNewRange(0, height, 0, HEIGHT_DISPLAY, waterLevelData)
    : null;

  const handleAlarmsView = () => {
    const usedPositions: number[] = [];
    const minSpacing = 10;

    const adjustedAlarms = alarms
      .sort((a, b) => a.level - b.level)
      .map((alarm) => {
        let value = Number(transferValueToNewRange(0, height, 0, HEIGHT_DISPLAY, alarm.level));
        let adjustedValue = value;

        for (const pos of usedPositions) {
          if (Math.abs(pos - adjustedValue) < minSpacing) {
            adjustedValue = pos + minSpacing;
          }
        }

        usedPositions.push(adjustedValue);
        return { alarm, adjustedValue };
      });

    return adjustedAlarms.map(({ alarm, adjustedValue }, index) => (
      <Box
        key={index}
        display="span"
        sx={{
          bottom: `${adjustedValue}%`,
          ...cardGraphicLabel,
          ...(isAlarmLevelValid(alarm) ? cardGraphicLabelRight : {}),
        }}
      >
        {isAlarmLevelValid(alarm) ? alarm.alarmTagName : ''}
      </Box>
    ));
  };

  const displayWaterLevelData = () => {
    if (!isNaN(waterLevelData) && validLevel) {
      return waterLevelData;
    } else {
      return '';
    }
  };

  return (
    <WidgetWrapper
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={waterLevelData !== null}
      isWetWell={true}
      timestamp={timestamp || initialLatestTimestamp}
    >
      <Box
        sx={{ ...cardGraphic, width: '150px', marginLeft: '75px' }}
        data-sm={waterLevelData ? 'data-available' : 'no-data'}
      >
        <Box sx={cardGraphicInner}>
          <Box display="span" sx={{ bottom: '100%', ...cardGraphicLabel, ...cardGraphicLabelLeft }}>
            {height} {unit}
          </Box>
          <Box display="span" sx={{ bottom: '0%', ...cardGraphicLabel, ...cardGraphicLabelLeft }}>
            0 {unit}
          </Box>

          <Box display="span" sx={() => cardGraphicLevel(waterLevelDisplay)} />
          <Box
            display="span"
            sx={() => cardGraphicText(waterLevelDisplay)}
            data-sm="wet-well-value"
          >
            {validLevel && displayWaterLevelData()} {unit}
          </Box>

          {handleAlarmsView()}
        </Box>
      </Box>
    </WidgetWrapper>
  );
};
